import React, { useEffect, useMemo, useState } from "react";
import EAButton from "../shared/Buttons/EAButton";

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed"; platform: string }>;
}

type Props = {
  /** ID sklepu – wstrzykniemy do linków (np. 1241). Jeśli nie podasz, spróbujemy pobrać z ?id */
  shopId?: string;
  /** Ścieżka startowa Twojej apki (bez domeny), np. "/testApptours" */
  appPath?: string;
  /** Tekst nagłówka */
  title?: string;
  /** Tekst lead (opis pod nagłówkiem) */
  lead?: string;
  /** Tekst na przycisku otwarcia aplikacji */
  openLabel?: string;
  /** Tekst na przycisku instalacji */
  installLabel?: string;
};

export default function InstallInstructions({
  shopId,
  appPath = "/testApp/login",
  title = "📲 Pobierz aplikację eAktywni",
  lead = "Kupuj bilety, rezerwuj zajęcia, sprawdzaj grafik i odbieraj powiadomienia — prosto z ekranu głównego.",
  openLabel = "Otwórz aplikację",
  installLabel = "📲 Zainstaluj aplikację",
}: Props) {
  // --- ID sklepu z propsa albo z URL ---
  const currentId = useMemo(() => {
    if (shopId) return shopId;
    const q = new URL(window.location.href).searchParams.get("id");
    return q && /^\d+$/.test(q) ? q : "1";
  }, [shopId]);

  // Link do Twojej apki (np. do listy zajęć/biletów) – z id klienta
  const base = (process.env.PUBLIC_URL || "").replace(/\/$/, "");
  const openUrl = `${base}${appPath}?id=${currentId}`;

  // --- install prompt (Android/desktop) ---
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isInstalled, setIsInstalled] = useState<boolean>(false);

  useEffect(() => {
    const standalone =
      window.matchMedia?.("(display-mode: standalone)")?.matches ||
      // iOS A2HS
      (navigator as any).standalone === true;
    setIsInstalled(!!standalone);

    const mql = window.matchMedia?.("(display-mode: standalone)");
    const onChange = (e: MediaQueryListEvent) => setIsInstalled(e.matches);
    mql?.addEventListener?.("change", onChange);

    const bip = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    };
    window.addEventListener("beforeinstallprompt", bip as EventListener);

    return () => {
      mql?.removeEventListener?.("change", onChange);
      window.removeEventListener("beforeinstallprompt", bip as EventListener);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) {
      alert("Jeśli przycisk się nie pojawia, użyj menu przeglądarki: „Dodaj do ekranu głównego”.");
      return;
    }
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    setDeferredPrompt(null);
    if (outcome === "accepted") setIsInstalled(true);
  };

  const ua = (navigator.userAgent || "").toLowerCase();
  const isIOS = /iphone|ipad|ipod/.test(ua);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const copyURL = async () => {
    try {
      await navigator.clipboard.writeText(openUrl);
      alert("Skopiowano link do aplikacji ✅");
    } catch {
      alert("Nie udało się skopiować linku.");
    }
  };

  return (
    <>
   
   
   
   
   
    <div style={styles.page}>
      {/* Header z gradientem */}
      <header style={styles.hero}>
        <div style={styles.heroInner}>
          <span style={styles.badge}>PWA</span>
          <h1 style={styles.h1}>{title}</h1>
          <p style={styles.lead}>{lead}</p>

          <div style={styles.ctaRow}>
            {/* <EAButton title={openLabel} onClick={() => (window.location.href = openUrl)} /> */}
            {!isInstalled && deferredPrompt && (
              <EAButton color="secondary" title={installLabel} onClick={handleInstall} />
            )}
            {/* {!deferredPrompt && !isIOS && !isInstalled && (
              <a href={openUrl} style={{ ...styles.btn, ...styles.btnGhost }}>
                Otwórz w przeglądarce
              </a>
            )}
            <button onClick={copyURL} style={{ ...styles.btn, ...styles.btnGhost }}>
              Kopiuj link
            </button> */}
          </div>

          {isInstalled && (
            <div style={styles.installedRow}>
              <span style={{ ...styles.badge, ...styles.badgeSuccess }}>Zainstalowano</span>
              Aplikacja jest już dodana do ekranu głównego. 🔥
            </div>
          )}
        </div>
      </header>

      <main style={styles.container}>
        {/* Korzyści */}
        <ul style={styles.benefits}>
          <li>⚡️ Kupuj bilety szybciej i wygodniej</li>
          <li>📅 Zapisuj się na zajęcia bez dzwonienia</li>
          <li>🔔 Powiadomienia o nowościach</li>
          <li>🏷️ Oferty tylko w aplikacji</li>
        </ul>

        <hr style={styles.divider} aria-hidden="true" />

        {/* Instrukcje */}
        <section>
          <h2 style={styles.h2}>Jak zainstalować?</h2>
          <p style={styles.muted}>
            Aplikacja działa jako <strong>PWA</strong>. Instalacja trwa chwilę i nie zajmuje prawie w ogóle miejsca.
          </p>

          {/* Android */}
          <article style={styles.card}>
            <header style={styles.cardHeader}>
              <span style={styles.platform}>🤖 Android</span>
              {!isInstalled && deferredPrompt && (
                <button onClick={handleInstall} style={{ ...styles.btn, ...styles.btnPrimary }}>
                  {installLabel}
                </button>
              )}
            </header>
            <div>
              <ol style={styles.steps}>
                <li>
                  Jeśli pojawi się baner „Zainstaluj aplikację” → kliknij i potwierdź (albo użyj przycisku powyżej).
                </li>
                <li>
                  Jeśli nie ma banera: w <b>Chrome</b> stuknij <b>⋮</b> (prawy górny róg) →{" "}
                  <b>„Dodaj do ekranu głównego”</b>.
                </li>
                <li>Ikona pojawi się na ekranie głównym – uruchamiaj jak zwykłą aplikację.</li>
              </ol>
              {deferredPrompt && !isInstalled && (
                <div style={styles.hint}>💡 Ten telefon wspiera instalację z przycisku powyżej.</div>
              )}
            </div>
          </article>

          {/* iOS */}
          <article style={styles.card}>
            <header style={styles.cardHeader}>
              <span style={styles.platform}>🍎 iOS (iPhone/iPad)</span>
            </header>
            <div>
              <ol style={styles.steps}>
                <li>
                  Otwórz tę stronę w <b>Safari</b> {isIOS ? "" : "(na iOS tylko Safari pozwala instalować PWA)"}.
                </li>
                <li>
                  Stuknij <b>Udostępnij</b> 📤 (kwadrat ze strzałką w górę) w dolnym pasku.
                </li>
                <li>
                  Wybierz <b>„Dodaj do ekranu początkowego”</b> i potwierdź.
                </li>
              </ol>
              <p style={styles.muted}>
                ℹ️ iOS nie pokazuje systemowego przycisku instalacji – instalacja zawsze przez menu <em>Udostępnij</em>.
              </p>
            </div>
          </article>
        </section>

        {/* FAQ */}
        <section>
          <h3 style={{ marginTop: 26 }}>Najczęstsze pytania</h3>
          <details style={styles.details}>
            <summary>Nie widzę opcji „Dodaj do ekranu głównego”</summary>
            <ul style={{ marginTop: 8 }}>
              <li>
                Na iOS użyj <b>Safari</b>.
              </li>
              <li>Na Androidzie sprawdź Chrome lub Edge.</li>
              <li>Zaktualizuj przeglądarkę do najnowszej wersji.</li>
            </ul>
          </details>
          <details style={styles.details}>
            <summary>Czy aplikacja zajmuje dużo miejsca?</summary>
            <p>Nie — PWA waży znacznie mniej niż klasyczne aplikacje.</p>
          </details>
          <details style={styles.details}>
            <summary>Czy muszę aktualizować aplikację?</summary>
            <p>
              Nie — aktualizuje się <strong>automatycznie</strong> przy kolejnym otwarciu.
            </p>
          </details>
        </section>
      </main>
    </div>
  </>
  );
}

/* ===== styles (inline, zero-dependency) ===== */
const styles: Record<string, React.CSSProperties> = {
  page: {
    minHeight: "100svh",
    background:
      "radial-gradient(1200px 600px at 80% 10%, rgba(59,130,246,.08), transparent 60%)," +
      "radial-gradient(1200px 600px at 10% 90%, rgba(99,102,241,.07), transparent 60%)," +
      "#f7f9fc",
  },
  hero: {
    padding: "max(24px, 4vh) clamp(16px,4vw,40px) 20px",
    background:
      "linear-gradient(135deg, rgba(59,130,246,.16), rgba(99,102,241,.14))",
    borderBottom: "1px solid rgba(15,23,42,.06)",
  },
  heroInner: {
    margin: "0 auto",
    maxWidth: "min(1100px, 92vw)",
  },
  container: {
    margin: "0 auto",
    maxWidth: "min(1100px, 92vw)",
    padding: "20px clamp(16px,4vw,40px) calc(48px + env(safe-area-inset-bottom))",
  },
  h1: {
    fontSize: "clamp(28px,4.5vw,42px)",
    margin: "6px 0 8px",
    letterSpacing: "-0.02em",
    color: "#0f172a",
  },
  lead: {
    fontSize: "clamp(16px,2.2vw,20px)",
    color: "#334155",
    margin: "0 0 16px",
  },
  badge: {
    display: "inline-block",
    padding: "5px 12px",
    borderRadius: 999,
    fontWeight: 700,
    fontSize: 12,
    background: "#eef2ff",
    color: "#3730a3",
  },
  badgeSuccess: { background: "#ecfdf5", color: "#065f46" },
  ctaRow: {
    display: "flex",
    gap: 12,
    flexWrap: "wrap",
    alignItems: "center",
    margin: "14px 0 6px",
  },
  installedRow: {
    marginTop: 8,
    fontSize: 15,
    color: "#065f46",
  },
  btn: {
    appearance: "none",
    border: "1px solid #e5e7eb",
    padding: "12px 16px",
    borderRadius: 12,
    fontWeight: 600,
    cursor: "pointer",
    textDecoration: "none",
    color: "#111827",
    background: "#fff",
  },
  btnPrimary: { background: "#111827", color: "#fff", borderColor: "#111827" },
  btnGhost: { background: "#fff" },
  benefits: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: "10px 14px",
    margin: "16px 0 0",
    padding: "0 0 0 18px",
    color: "#0f172a",
  },
  divider: { border: 0, borderTop: "1px dashed #e5e7eb", margin: "28px 0" },
  muted: { color: "#6b7280", fontSize: 14.5 },
  card: {
    border: "1px solid #e5e7eb",
    borderRadius: 16,
    padding: 14,
    margin: "14px 0",
    background: "#fff",
    boxShadow: "0 8px 20px rgba(0,0,0,0.03)",
  },
  cardHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 6,
  },
  platform: { fontWeight: 800, letterSpacing: ".01em" },
  steps: { margin: "8px 0 0 18px" },
  details: {
    border: "1px solid #e5e7eb",
    borderRadius: 12,
    padding: "10px 12px",
    margin: "8px 0",
    background: "#fcfcfd",
  },
  hint: { marginTop: 8, fontSize: 14.5, color: "#0f766e" },
};
